/**
 * THE BRAND AXIS — a school's colour as a third theme axis.
 *
 * The design system has two axes (spec §1.2): `data-theme` on <html> is the
 * DIRECTION and it is a preference; `data-audience` is the year/role and it is
 * DATA. This adds `data-brand`, also on <html>, also data: it comes from the
 * institution the caller is acting in, never from a toggle. A school buying
 * seats gets its colour on the buttons and links of the admin console and its
 * learners' app.
 *
 * WHAT IT OVERRIDES, AND WHAT IT MUST NOT. Only the accent ROLES — the fill,
 * the text-on-fill, the dimmed fill, and the admin audience colour, which IS
 * the accent by definition (spec §4.3). Surfaces, reading text, muted text,
 * status colours, the borders and the year progression stay Cusp's, because
 * they are the contrast contract and the product's own vocabulary; a school
 * that could set `--color-bg` could make its learners' transcript unreadable.
 * The CUSP wordmark's gold `em` stays gold too (`--color-cusp-gold`): this is
 * co-branding, not white-labelling.
 *
 * ONE INPUT, EVERYTHING ELSE DERIVED. A school gives one primary colour. If it
 * also gave us "the colour for links on cream" we would be trusting a customer
 * to hold a contrast bar the spec says we hold — and `#2e3a87` on `#f4efe6` is
 * fine while `#767676` is not, which is not a distinction an office manager
 * picking a colour should have to make. So `deriveBrand` computes the readable
 * variants and the guarantees below are ours, not theirs.
 */

import { type Theme } from '@cusp/core';
import { contrast, hexToOklch, isHexColor, normalizeHex, oklchToHex } from './color.js';

/**
 * The three grounds any text can land on, per direction, in the order
 * `--color-bg`, `--color-surface`, `--color-elevated`.
 *
 * DUPLICATED FROM `styles.css` ON PURPOSE, AND GUARDED. The derivation runs in
 * JavaScript and the grounds live in CSS, and there is no way to read a
 * stylesheet variable that is not currently applied to an element — the whole
 * point is to measure the OTHER direction's grounds while rendering this one.
 * So the values are restated here and `tokens.test.ts` asserts this constant
 * equals what it parses out of `styles.css`. Move a ground and the test fails
 * rather than a school's links quietly dropping under 4.5:1.
 */
export const GROUNDS: Record<Theme, readonly [string, string, string]> = {
  obsidian: ['#0a0a0a', '#1a1a1a', '#2a2a2a'],
  canvas: ['#f4efe6', '#fbf8f2', '#ffffff'],
};

/**
 * Cusp's own accent fill — `--color-cusp-gold-fill` in `styles.css`, the one
 * gold that is identical in every direction.
 *
 * RESTATED HERE FOR THE SAME REASON `GROUNDS` IS, and guarded the same way by
 * `tokens.test.ts`. A screen that lets an administrator CLEAR their school's
 * colour has to be able to show what clearing it looks like, and "what the
 * document is currently painted in" is exactly the wrong source for that — the
 * console is painted in the school's colour at the moment the question is
 * asked. There is no way to read a stylesheet variable that is not applied,
 * so the value is restated and the test joins the two.
 */
export const CUSP_GOLD_FILL = '#c5a044';

/** WCAG AA for body-sized text. The bar every derived text colour clears. */
export const AA_TEXT = 4.5;

/** The near-black Cusp uses on the gold — `--color-on-accent` in `styles.css`. */
const NEAR_BLACK = '#0a0a0a';
const PURE_BLACK = '#000000';
const PURE_WHITE = '#ffffff';

/** What a brand resolves to. Four values; everything else is `color-mix()`. */
export interface BrandTokens {
  /** The school's colour, unchanged — a fill carries its own text (spec §2.1). */
  fill: string;
  /** Text sitting ON the fill. */
  onFill: string;
  /** The school's colour as glyphs, on Obsidian's dark grounds. */
  textDark: string;
  /** The school's colour as glyphs, on Canvas's light grounds. */
  textLight: string;
}

/**
 * Text that can be read on `fill`.
 *
 * Near-black first, because that is what Cusp's own gold chip uses and a
 * school's chip should look like the product's. Then pure white, then pure
 * BLACK — and the pure ones are what make this a guarantee rather than a
 * hope. For any colour P, `contrast(white, P) × contrast(P, black) = 21`
 * exactly, so at least one of them measures √21 = 4.58:1. With `#0a0a0a` in
 * place of black the product is 19.8 and the guarantee is gone: a mid grey
 * like `#777777` measures 4.48:1 against white and 4.42:1 against `#0a0a0a`
 * and fails both bars (it clears 4.69:1 against pure black).
 *
 * Exported because a swatch for some OTHER tenant's colour needs it too — an
 * account-menu workspace row cannot borrow the acting tenant's
 * `--color-on-accent`.
 */
export function onFillFor(primary: string): string {
  const fill = normalizeHex(primary);
  if (contrast(NEAR_BLACK, fill) >= AA_TEXT) return NEAR_BLACK;
  if (contrast(PURE_WHITE, fill) >= AA_TEXT) return PURE_WHITE;
  return PURE_BLACK;
}

/** The worst of the three grounds. */
function minContrast(hex: string, grounds: readonly string[]): number {
  return Math.min(...grounds.map((ground) => contrast(hex, ground)));
}

/**
 * The school's colour, moved along the OKLCH lightness axis until it is
 * readable on every ground of `theme`.
 *
 * THE MINIMUM OVER ALL THREE GROUNDS, not the one that looks limiting. It is
 * tempting to reason "for dark text the hardest ground is white" and measure
 * only that — and it is wrong: Canvas's page background is the cream `#f4efe6`,
 * not white, and `#767676` measures 4.54:1 on white and 3.97:1 on cream. A
 * link that passes the elevated surface and fails the page is worse than one
 * that fails everywhere, because it passes review.
 *
 * TERMINATION IS BY ENDPOINT, NOT BY MONOTONICITY. Contrast against three
 * grounds at once is not guaranteed to improve monotonically as L moves (the
 * gamut mapping bends chroma on the way), so a bisection cannot be justified
 * by "the predicate flips once". What justifies it is that the ENDPOINT always
 * passes — white measures 14.35:1 on Obsidian's lightest ground `#2a2a2a`, black
 * measures 18.34:1 on Canvas's darkest `#f4efe6` — and the loop only ever moves
 * `hi` to a value it has just measured as passing. So `hi` passes at every
 * step, and 24 halvings of the L axis is far below one 8-bit step.
 *
 * Every candidate is measured as its QUANTIZED HEX, not as a float: the browser
 * only ever renders the hex, so measuring the float would be measuring a colour
 * nobody sees.
 */
function deriveText(primary: string, theme: Theme): string {
  const grounds = GROUNDS[theme];
  if (minContrast(primary, grounds) >= AA_TEXT) return primary;

  // Dark grounds want a LIGHTER accent, light grounds a darker one.
  const endpointL = theme === 'obsidian' ? 1 : 0;
  const base = hexToOklch(primary);
  // Hue is held exactly; chroma is reduced only where the (l, c, h) triple
  // leaves the sRGB gamut — see `oklchToHex`.
  const candidate = (l: number): string => oklchToHex({ l, c: base.c, h: base.h });
  const passes = (hex: string): boolean => minContrast(hex, grounds) >= AA_TEXT;

  let lo = base.l; // measured as failing above
  let hi = endpointL; // passes by construction
  for (let step = 0; step < 24; step++) {
    const mid = (lo + hi) / 2;
    if (passes(candidate(mid))) hi = mid;
    else lo = mid;
  }

  const result = candidate(hi);
  if (passes(result)) return result;

  // Unreachable while the endpoints above hold, and kept because that
  // reasoning depends on GROUNDS: if a ground ever moves far enough to break
  // it, walk the rest of the way rather than returning something unreadable.
  for (let i = 1; i <= 32; i++) {
    const stepped = candidate(hi + ((endpointL - hi) * i) / 32);
    if (passes(stepped)) return stepped;
  }
  return endpointL === 1 ? PURE_WHITE : PURE_BLACK;
}

/**
 * One school colour → the four values the stylesheet consumes.
 *
 * `fill` is the input verbatim. A fill has nothing behind it, so nothing
 * constrains it — the same reasoning that makes Cusp's own `#c5a044` a brand
 * constant across both directions (spec §2.1). The school's colour therefore
 * appears exactly as given wherever it is a chip, a bar or a button, and only
 * the GLYPH variants move.
 *
 * Throws on anything that is not a 6-digit hex; callers holding a server value
 * guard with `isBrandPrimary` first.
 */
export function deriveBrand(primary: string): BrandTokens {
  const fill = normalizeHex(primary);
  return {
    fill,
    onFill: onFillFor(fill),
    textDark: deriveText(fill, 'obsidian'),
    textLight: deriveText(fill, 'canvas'),
  };
}

/** Is this a value `deriveBrand` will accept? */
export function isBrandPrimary(value: unknown): value is string {
  return typeof value === 'string' && isHexColor(value);
}

// ── Applying it to the document ───────────────────────────────────────

/**
 * The four inline custom properties. INLINE, not a stylesheet rule, because an
 * element's own `style` beats every selector — so `:root[data-brand]` in
 * `styles.css` can read these without anyone having to win a specificity
 * argument, and clearing them is `removeProperty`, not a second rule.
 */
const BRAND_PROPERTIES = [
  '--brand-accent-fill',
  '--brand-on-accent',
  '--brand-accent-text-dark',
  '--brand-accent-text-light',
] as const;

/**
 * A brand ON THE DOCUMENT: whose it is, what it is called, and what it paints.
 *
 * THE OWNER IS WRITTEN DOWN BECAUSE IT CANNOT BE INFERRED. The slug is what the
 * stylesheet and the e2e probes select on, and for a while it was the only
 * thing recorded — the institution a paint belonged to was taken to be
 * "whoever is acting now". That inference is wrong exactly when it costs
 * something, because three parties paint this document and none of them meet:
 * the pre-paint script (from the cache, before React exists), `useBrandSync`,
 * and StrictMode's replay of a cleanup that has already torn a brand down.
 *
 * The concrete failure needs no hand-edited storage. The script paints school
 * A because the cache matched the stored identity AT THAT INSTANT; a moment
 * later `adoptSessionIdentity` (`@cusp/api-client`, `src/session.ts`) replaces
 * an invalid stored selection with the live session's sole membership, or a
 * second tab rewrites the stored identity — and the hook mounts in school B
 * with its read still pending. "Pending leaves whatever is applied alone" then
 * wins and A's colour frames B's data for as long as B's read takes, which an
 * error response makes forever. With the owner on the document that stops
 * being an inference and becomes a comparison.
 */
export interface AppliedBrand {
  /** The institution the paint belongs to — `data-brand-tenant`. */
  tenantId: string;
  /** Its slug — `data-brand`, the attribute the stylesheet selects on. */
  slug: string;
  tokens: BrandTokens;
}

/**
 * Put a school's brand on the document.
 *
 * TWO ATTRIBUTES AND FOUR PROPERTIES, AND THAT IS A CONTRACT WITH THE PRE-PAINT
 * SCRIPT. The inline script in each app's index.html applies the cached brand
 * before React exists, and it has to set `data-brand` (the slug, for CSS),
 * `data-brand-tenant` (the id, for ownership) and all four properties — the
 * cache entry carries `tenantId` precisely so that it can. A script that sets
 * only `data-brand` paints something whose ownership nobody can establish, and
 * `readAppliedBrand()` reads that as no brand rather than guessing.
 *
 * Deliberately does NOT touch the theme store, even though a brand carries a
 * default direction: this module is imported BY `theme.ts` (so `initTheme()`
 * can read the cache below before it applies an attribute), and a store call
 * here would close that into a cycle. The caller — `useBrandSync`, or the
 * pre-paint script — applies the brand and sets the session default together.
 */
export function applyBrand(brand: AppliedBrand): void {
  const root = document.documentElement;
  root.dataset['brand'] = brand.slug;
  root.dataset['brandTenant'] = brand.tenantId;
  root.style.setProperty('--brand-accent-fill', brand.tokens.fill);
  root.style.setProperty('--brand-on-accent', brand.tokens.onFill);
  root.style.setProperty('--brand-accent-text-dark', brand.tokens.textDark);
  root.style.setProperty('--brand-accent-text-light', brand.tokens.textLight);
}

/** Remove every trace of a brand — back to Cusp gold, in one frame. */
export function clearBrand(): void {
  const root = document.documentElement;
  delete root.dataset['brand'];
  delete root.dataset['brandTenant'];
  for (const property of BRAND_PROPERTIES) root.style.removeProperty(property);
}

/** The slug currently painted, or null. */
export function appliedBrand(): string | null {
  return document.documentElement.dataset['brand'] ?? null;
}

/**
 * The institution whose brand is currently painted, or null.
 *
 * Read on its own rather than through `readAppliedBrand()` because ownership
 * has to stay decidable when the paint is half-torn, malformed, or came from a
 * build that predates the owner attribute: "this is not mine" and "I cannot
 * tell whose this is" have the same right answer, which is to take it down.
 */
export function appliedBrandTenant(): string | null {
  return document.documentElement.dataset['brandTenant'] ?? null;
}

/**
 * The brand currently on the document, read back off it — the argument
 * `applyBrand` was called with, whoever called it.
 *
 * THE DOCUMENT IS THE ONLY HONEST SOURCE HERE, which is the point. A brand can
 * be on <html> before any React code has run (the pre-paint script puts it
 * there from the cache), so a caller that needs to put back what it just tore
 * down — `useBrandSync`, under StrictMode's mount/cleanup/mount replay — cannot
 * ask itself what it applied. It has to ask the document, and it has to do so
 * BEFORE clearing. It gets the owner back with it, so the answer can be checked
 * against the tenant that is actually acting rather than assumed to be it.
 *
 * Anything short of a complete, well-formed set reads as no brand: a partially
 * cleared document, or one whose owner was never recorded, is not something
 * worth restoring.
 */
export function readAppliedBrand(): AppliedBrand | null {
  const root = document.documentElement;
  const slug = root.dataset['brand'];
  const tenantId = root.dataset['brandTenant'];
  if (slug === undefined || slug === '') return null;
  if (tenantId === undefined || tenantId === '') return null;
  const [fill, onFill, textDark, textLight] = BRAND_PROPERTIES.map((property) =>
    root.style.getPropertyValue(property).trim(),
  ) as [string, string, string, string];
  if (![fill, onFill, textDark, textLight].every(isBrandPrimary)) return null;
  return { tenantId, slug, tokens: { fill, onFill, textDark, textLight } };
}

// ── More than one syncer at a time ────────────────────────────────────

/**
 * What a departing syncer leaves for the next one to pick up.
 *
 * THE PAINT IS NOT THE ONLY THING A DEPARTURE TAKES DOWN. A syncer's cleanup
 * clears the brand AND the school's default direction, and those two are
 * separable: a surface a school may not paint (the front door — `paint: false`
 * in `useBrandSync`) holds no colour and still holds a direction. So `brand` is
 * nullable, and a handover carrying nothing but a direction is a real one.
 */
export interface BrandHandover {
  /**
   * Whose handover this is — the key `takeBrandHandover` matches on.
   *
   * The departing syncer reads it off the PAINT's own owner where there is one,
   * because the document is the honest source: on a reload the paint is the
   * pre-paint script's work and no hook ever saw it applied. Where nothing is
   * painted it is the DIRECTION's owner, which the theme store records for
   * exactly this reason (`SessionDefault` in `theme.ts`) — a `paint: false`
   * surface hands over a direction and nothing else, and keying that on
   * "whoever was mounted" would hand one school's direction to another. Only
   * when neither is owned does the departing syncer's own tenant decide.
   */
  tenantId: string;
  /** What was on the document, or null when nothing was. */
  brand: AppliedBrand | null;
  /** The session default direction that went with it; `theme.ts` owns the store. */
  defaultTheme: Theme | null;
}

/**
 * WHO IS ALLOWED TO TAKE THE BRAND DOWN.
 *
 * `useBrandSync` clears the document on unmount, because a shell leaving the
 * authenticated surface must not carry a school's colour out onto the landing
 * page. That is right for the only syncer on a page and wrong the instant
 * there are two: one layout unmounting while another stays mounted strips a
 * brand the survivor is still responsible for, and the survivor's dependencies
 * have not changed, so nothing ever puts it back. The document goes gold under
 * a school's data and stays there.
 *
 * So the document is not owned by a component, it is owned by the SET of live
 * syncers. Each takes a token on mount and gives it back on cleanup, and only
 * the release that empties the set actually clears. This lives here, in the
 * React-free store, because it is a fact about the document rather than about
 * a component tree, and because the hook has to consult it from inside a
 * layout cleanup — not a place to be reading state through React.
 *
 * It is a safety net and not a design: see `useBrandSync`, which asks an app to
 * mount exactly one.
 */
const liveSyncers = new Set<symbol>();

/**
 * What the last syncer out left behind, keyed by the tenant that OWNED it.
 *
 * Two arrivals want it, and neither is exotic. StrictMode replays a mount as
 * cleanup → setup to prove a component survives being remounted, and our
 * cleanup is the unmount clear, so without a handover every dev-mode load of a
 * branded school renders unbranded for the whole initial fetch — the exact
 * flash the cache exists to prevent, arriving from the code that prevents it.
 * And a route swap commits a departure and an arrival together, so a syncer can
 * find the document blank through no fault of its own.
 *
 * Keyed by owner and CONSUMED rather than read: a handover is only ever applied
 * back to the tenant it was taken from, and only once.
 */
let handover: BrandHandover | null = null;

/** Take a share of the document's brand; the token is the receipt. */
export function registerBrandSyncer(): symbol {
  const token = Symbol('brand-syncer');
  liveSyncers.add(token);
  return token;
}

/** Give it back. True when this was the LAST one — i.e. the caller must clear. */
export function releaseBrandSyncer(token: symbol): boolean {
  liveSyncers.delete(token);
  return liveSyncers.size === 0;
}

/**
 * Leave what is about to be torn down where the next syncer can find it. `null`
 * clears the handover: there is nothing to hand over, and a stale one is worse
 * than none.
 */
export function stashBrandHandover(next: BrandHandover | null): void {
  handover = next;
}

/**
 * The handover, if there is one and it belongs to `tenantId`.
 *
 * Always consumes, even when it does not match. A handover that has been
 * offered once is spent; one left lying around would surface at some later,
 * unrelated mount and paint a school nobody asked for.
 */
export function takeBrandHandover(tenantId: string | null): BrandHandover | null {
  const saved = handover;
  handover = null;
  if (saved === null || tenantId === null || saved.tenantId !== tenantId) return null;
  return saved;
}

// ── The first-paint cache ─────────────────────────────────────────────

/**
 * A GUESS FOR THE FIRST PAINT, NEVER SERVER TRUTH.
 *
 * The brand arrives from `GET /v1/me/branding`, which is a round trip; without
 * a cache every reload paints Cusp gold and then repaints the school's colour,
 * which is precisely the flash `data-theme`'s inline script exists to prevent.
 * So the last-seen brand is written here and a tiny script in each app's
 * index.html applies it before first paint. It can be one save stale, and that
 * is accepted: the query corrects it within the request, and the alternative
 * is a gold flash on every load for every branded school.
 *
 * The logo is deliberately NOT cached — it is a data URI up to 256 KB and
 * localStorage is a ~5 MB origin-wide budget shared with everything else.
 */
export const BRAND_CACHE_KEY = 'cusp.brand';

/**
 * Mirrors `@cusp/api-client`'s own `STORAGE_KEY` (`src/identity.ts`).
 *
 * Restated rather than imported: `@cusp/theme` is the bottom of the front-end
 * stack — the api-client imports the contracts and the query layer, and a
 * stylesheet package that pulled all of that in to read one string would be
 * upside down. `theme.test.ts` pins the literal, and the identity store's own
 * suite pins its side, so a rename fails on both ends.
 */
export const DEV_IDENTITY_STORAGE_KEY = 'cusp.dev-identity';

export interface BrandCacheEntry {
  tenantId: string;
  slug: string;
  tokens: BrandTokens;
  defaultTheme: Theme | null;
}

function isBrandTokens(value: unknown): value is BrandTokens {
  if (typeof value !== 'object' || value === null) return false;
  const o = value as Record<string, unknown>;
  return (['fill', 'onFill', 'textDark', 'textLight'] as const).every((key) =>
    isBrandPrimary(o[key]),
  );
}

export function writeBrandCache(entry: BrandCacheEntry): void {
  try {
    window.localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Private modes and full quotas both throw. A missing cache costs one
    // flash on the next load, never correctness.
  }
}

/**
 * The cached brand, but only if it belongs to `tenantId`.
 *
 * The tenant check is the whole point: a stale entry from the workspace the
 * user switched away from would paint the wrong school's colour on the first
 * frame, which is worse than the gold it replaced. Anything unparseable, of
 * the wrong shape, or for another tenant reads as "no cache".
 */
export function readBrandCache(tenantId: string | null): BrandCacheEntry | null {
  if (tenantId === null) return null;
  try {
    const raw = window.localStorage.getItem(BRAND_CACHE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const o = parsed as Record<string, unknown>;
    if (o['tenantId'] !== tenantId) return null;
    if (typeof o['slug'] !== 'string' || !isBrandTokens(o['tokens'])) return null;
    const theme = o['defaultTheme'];
    return {
      tenantId,
      slug: o['slug'],
      tokens: o['tokens'],
      defaultTheme: theme === 'obsidian' || theme === 'canvas' ? theme : null,
    };
  } catch {
    return null;
  }
}

export function clearBrandCache(): void {
  try {
    window.localStorage.removeItem(BRAND_CACHE_KEY);
  } catch {
    /* nothing to do — the cache is advisory */
  }
}

/**
 * The tenant of the stored identity, read WITHOUT importing the identity store.
 *
 * `initTheme()` runs before React, and before the api-client's store is
 * necessarily loaded, and it needs to know which tenant's cached brand is
 * relevant. Anyone can hand-edit localStorage, so this validates rather than
 * trusts: both the current `{ identity, source }` envelope and the bare
 * identity that predates it are accepted, anything else reads as no identity.
 */
export function storedTenantId(): string | null {
  try {
    const raw = window.localStorage.getItem(DEV_IDENTITY_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const envelope = parsed as Record<string, unknown>;
    const identity = (
      typeof envelope['identity'] === 'object' && envelope['identity'] !== null
        ? envelope['identity']
        : envelope
    ) as Record<string, unknown>;
    return typeof identity['tenantId'] === 'string' ? identity['tenantId'] : null;
  } catch {
    return null;
  }
}

/**
 * What a branding read gives the theme layer.
 *
 * Structural and theme-owned rather than imported from `@cusp/contracts`: the
 * wire schema lands in a later PR, and a stylesheet package that depends on the
 * HTTP contracts to describe three fields has acquired a dependency it will
 * never shed. The hook takes whatever matches this shape.
 */
export interface BrandInput {
  primary: string | null;
  logo: string | null;
  defaultTheme: Theme | null;
}
